// Termos de uso e Política de privacidade. O texto sai de @truco6/legal, em Markdown, com os valores de @truco6/planos e
// @truco6/campeonatos: o site (trucode6.com.br/termos e /privacidade) mostra o mesmo texto.
import { Fragment, type ReactNode, useEffect, useRef, useState } from 'react';
import { type Bloco, type Trecho, lerDocumento, privacidadeMarkdown, termosMarkdown } from '@truco6/legal';
import { voltar } from '../comum/rotas.ts';
import { Tela, Topo } from './comum.tsx';

// Reabre a barra de cookies (packages/medicao). Só existe em trucode6.com.br.
function MudarCookies() {
  const t6Cookies = typeof window === 'undefined' ? undefined : window.t6Cookies;
  if (!t6Cookies) return null;
  return <button className="btn2" onClick={() => t6Cookies.abrir()}>Mudar minha escolha de cookies</button>;
}

// O texto em até 720 px. No computador, o sumário fica ao lado, montado dos títulos das seções.
function Documento({ titulo, atualizado, children }: { titulo: string; atualizado: string; children: ReactNode }) {
  const texto = useRef<HTMLElement>(null);
  const [secoes, setSecoes] = useState<{ id: string; titulo: string }[]>([]);
  useEffect(() => {
    const titulos = [...(texto.current?.querySelectorAll('h2') ?? [])];
    setSecoes(titulos.map((h, i) => {
      if (!h.id) h.id = 'secao-' + (i + 1);
      return { id: h.id, titulo: h.textContent ?? '' };
    }));
  }, []);
  return (
    <Tela larga>
      <Topo titulo={titulo} voltar={voltar} />
      <div className="documento">
        <article className="legal" ref={texto}>
          <p className="nota">Atualizado em {atualizado}.</p>
          {children}
        </article>
        {secoes.length > 0 && (
          <nav className="sumario-legal" aria-label="Sumário">
            <p className="rotulo">Sumário</p>
            <ul>
              {secoes.map((x) => (
                <li key={x.id}>
                  <a href={'#' + x.id} onClick={(ev) => { ev.preventDefault(); document.getElementById(x.id)?.scrollIntoView({ block: 'start' }); }}>{x.titulo}</a>
                </li>
              ))}
            </ul>
          </nav>
        )}
      </div>
    </Tela>
  );
}

function Trechos({ trechos }: { trechos: Trecho[] }) {
  return trechos.map((t, i) => {
    if (t.t === 'negrito') return <b key={i}>{t.texto}</b>;
    if (t.t === 'link') return <a key={i} href={t.href}>{t.texto}</a>;
    return <Fragment key={i}>{t.texto}</Fragment>;
  });
}

function BlocoLegal({ b }: { b: Bloco }) {
  switch (b.tipo) {
    case 'h1': case 'h2': return <h2>{b.texto}</h2>;
    case 'h3': return <h3>{b.texto}</h3>;
    case 'p': return <p><Trechos trechos={b.trechos} /></p>;
    case 'ul': return <ul>{b.itens.map((item, i) => <li key={i}><Trechos trechos={item} /></li>)}</ul>;
    case 'tabela': return (
      <table className="tabela-legal">
        <thead><tr>{b.cabecalho.map((c, i) => <th key={i}>{c}</th>)}</tr></thead>
        <tbody>{b.linhas.map((l, i) => <tr key={i}>{l.map((c, j) => <td key={j}>{c}</td>)}</tr>)}</tbody>
      </table>
    );
  }
}

// O documento de @truco6/legal na tela, com o título da barra de cima. `depois` entra no fim da seção que tem esse título.
function DocumentoLegal({ titulo, md, depois }: { titulo: string; md: string; depois?: { secao: string; no: ReactNode } }) {
  const { atualizado, blocos } = lerDocumento(md);
  let naSecao = false;
  const nos = blocos.flatMap((b, i) => {
    const fecha = b.tipo === 'h2' && naSecao;
    if (b.tipo === 'h2') naSecao = !!depois && b.texto.endsWith(depois.secao);
    const no = <BlocoLegal key={i} b={b} />;
    return fecha ? [<Fragment key={'d' + i}>{depois!.no}</Fragment>, no] : [no];
  });
  return <Documento titulo={titulo} atualizado={atualizado ?? ''}>{nos}</Documento>;
}

export function Termos() {
  return <DocumentoLegal titulo="Termos de uso" md={termosMarkdown()} />;
}

export function Privacidade() {
  return <DocumentoLegal titulo="Privacidade" md={privacidadeMarkdown()} depois={{ secao: 'Armazenamento no aparelho', no: <MudarCookies /> }} />;
}
